import {html, HTMLTemplateResult} from "lit";
import {customElement} from "lit/decorators/custom-element.js";
import {property} from "lit/decorators/property.js";

import {KnowledgeObject} from "../../../domain/entities/knowledge-object.entity";
import {renderKnowledgeFilterToggleButton} from "../../../helpers/knowledge-filter-toggle.helper";
import {AbstractLitElement} from "../../abstract-lit.element";

// Side-effect imports register sub-elements so their tags resolve when rendered.
import "../knowledge-empty-state/knowledge-empty-state.component";
import "../knowledge-whiteboard/knowledge-whiteboard.component";
import "../knowledge-rte/knowledge-rte.component";
import "../knowledge-image/knowledge-image.component";

/**
 * Polymorphic right-hand pane of the Knowledge view. Dispatches rendering
 * based on the selected KnowledgeObject's `type`. Light DOM per ADR-007.
 *
 * The `whiteboard`/`document` branches carry the Knowledge filter-drawer
 * toggle button inside their OWN toolbar (`knowledge-whiteboard`/
 * `knowledge-rte`, see `knowledge-filter-toggle.helper.ts`). The other two
 * branches (`image`, no item) have no toolbar of their own at all, so this
 * component renders the SAME toggle button itself as a fallback — Stefan,
 * 2026-08-30: the filter drawer must stay reachable regardless of which
 * object type (or none) is currently open, not just while a whiteboard/
 * document happens to be shown.
 */
@customElement('knowledge-view-pane')
export class KnowledgeViewPane extends AbstractLitElement {

    @property({type: Object})
    public item: KnowledgeObject | null = null;

    protected render(): HTMLTemplateResult {
        const item = this.item;
        if (!item) {
            return html`
                <div class="knowledge-view-pane__toolbar-fallback">${renderKnowledgeFilterToggleButton()}</div>
                <knowledge-empty-state></knowledge-empty-state>
            `;
        }

        if (item.type === 'whiteboard') {
            return html`<knowledge-whiteboard .item=${item}></knowledge-whiteboard>`;
        }

        if (item.type === 'document') {
            return html`<knowledge-rte .item=${item}></knowledge-rte>`;
        }

        if (item.type === 'image') {
            return html`
                <div class="knowledge-view-pane__toolbar-fallback">${renderKnowledgeFilterToggleButton()}</div>
                <knowledge-image .item=${item}></knowledge-image>
            `;
        }

        return html`
            <div class="knowledge-view-pane__toolbar-fallback">${renderKnowledgeFilterToggleButton()}</div>
            <knowledge-empty-state></knowledge-empty-state>
        `;
    }
}
